import React, { useState } from 'react';
import {
    auth,
    googleProvider,
    appleProvider,
    signInWithPopup,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    sendEmailVerification,
    sendPasswordResetEmail
} from '../Firebase';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import '../css/Login.css';
import google_icon from '../assets/google.png';
import apple_icon from '../assets/apple.png';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isRegistering, setIsRegistering] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const navigate = useNavigate(); // Initialize navigate function

    const blockedDomains = [
        'yopmail.com',
        'tempmail.com',
        '10minutemail.com',
        'mailinator.com',
        'guerrillamail.com',
    ];

    const isBlockedDomain = (email) => {
        const emailDomain = email.split('@')[1].toLowerCase();
        return blockedDomains.includes(emailDomain);
    };

    // Handle form submission for login/signup
    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (isBlockedDomain(email)) {
            setError('Temporary email addresses are not allowed.');
            return;
        }

        try {
            if (isRegistering) {
                // Sign up with email and password
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                const user = userCredential.user;

                // Send email verification
                await sendEmailVerification(user);
                setSuccess('A verification email has been sent. Please verify your email to complete signup.');
            } else {
                // Log in with email and password
                const userCredential = await signInWithEmailAndPassword(auth, email, password);
                const user = userCredential.user;

                // Check if the user's email is verified
                if (!user.emailVerified) {
                    setError('Email is not verified. Please check your inbox for the verification email.');
                    return;
                }

                setSuccess('Logged in successfully.');
                //navigate('/'); // Redirect to the landing page
                navigateScreen();
            }
        } catch (err) {
            setError(getErrorMessage(err.code));
        }
    };

    const handleGoogleLogin = async () => {
        setError('');
        setSuccess('');
        try {
            await signInWithPopup(auth, googleProvider);
            setSuccess('Logged in successfully with Google.');
            //navigate('/'); // Redirect to the landing page
            navigateScreen();
        } catch (err) {
            setError(getErrorMessage(err.code));
        }
    };

    const appleSignIn = async () => {
        setError('');
        setSuccess('');
        try {
            const result = await signInWithPopup(auth, appleProvider);

            // Retrieve the user's information
            const user = result.user;
            console.log("User Info:", user);
            //navigate('/'); // Redirect to the landing page
            navigateScreen();
            // You can handle user sign-in or save the user info in the database here.
        } catch (error) {
            setError(getErrorMessage(error.code));
        }
    };


    const handleForgotPassword = () => {
        if (!email) {
            setError("Please enter your email address to reset your password.");
            return;
        }

        //const auth = getAuth();
        sendPasswordResetEmail(auth, email)
            .then(() => {
                setSuccess("Password reset email sent! Please check your inbox.");
                setError(""); // Clear any previous errors
            })
            .catch((error) => {
                setError(error.message || "Failed to send password reset email.");
            });
    };



    const navigateScreen = () => {

        setTimeout(() => {
            const redirectPath = localStorage.getItem("redirectAfterLogin") || "/";
            localStorage.removeItem("redirectAfterLogin"); // Clear it after redirection

            if (redirectPath.startsWith("//buy.stripe.com")) {
                window.location.href = redirectPath; // Redirect to Stripe
            } else {
                navigate(redirectPath); // Navigate normally
            }
        }, 1000); // Simulating login delay        
    }

    const getErrorMessage = (errorCode) => {
        console.log(errorCode);
        const errorMessages = {
            'auth/email-already-in-use': 'The email address is already in use. Please try logging in.',
            'auth/invalid-email': 'The email address is not valid. Please enter a valid email.',
            'auth/invalid-credential': 'The email or password is incorrect. Please try again.',
            'auth/user-not-found': 'No account found with this email. Please sign up first.',
            'auth/weak-password': 'The password is too weak. Please enter a stronger password.',
        };

        return errorMessages[errorCode] || 'An unexpected error occurred. Please try again.';
    };

    return (
        <div className="login-page">
            <div className="login-container">
                <div className="login-header">
                    <h1>Login/Signup</h1>
                    <p>
                        to unlock <strong>Smart Soccer Search Powered by AI</strong>
                    </p>
                </div>

                <div className="login-form">
                    <form onSubmit={handleFormSubmit}>
                        <div className="form-group">
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                placeholder="Enter your email"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="password">Password</label>
                            <input
                                type="password"
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                placeholder="Enter your password"
                            />
                        </div>
                        {error && <p className="error-message">{error}</p>}
                        {success && <p className="success-message">{success}</p>}
                        <button type="submit" className="btn btn-primary btn-login">
                            {isRegistering ? 'Sign Up' : 'Log In'}
                        </button>
                    </form>

                    <div className="forgot-password">
                        <p>
                            Forgot your password?{' '}
                            <span onClick={handleForgotPassword} className="forgot-password-link">
                                Reset it here
                            </span>
                        </p>
                    </div>

                    <div className="toggle-mode">
                        {isRegistering ? (
                            <p>
                                Already have an account?{' '}
                                <span onClick={() => setIsRegistering(false)}>Log In</span>
                            </p>
                        ) : (
                            <p>
                                Don't have an account?{' '}
                                <span onClick={() => setIsRegistering(true)}>Sign Up</span>
                            </p>
                        )}
                    </div>

                    <div className="google-login">
                        <p>Or login using</p>
                        <button onClick={handleGoogleLogin} className="btn btn-primary btn-google">
                            <img src={google_icon} alt="Google" width="32" height="32" /> Login with Google
                        </button>

                        <button onClick={appleSignIn} className="btn btn-primary btn-google btn-apple">
                            <img src={apple_icon} alt="Apple" width="32" height="32" /> Login with Apple
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
